import { Box, BoxProps, Fade, Flex } from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { LocationOptions, Virtuoso, VirtuosoHandle } from "react-virtuoso";

import { Button } from "../../../../components";
import useDebouncedCallback from "../../../../hooks/useDebouncedCallback";
import {
  CallNoteFragment,
  CallSpeakerFragment,
  CallSpeakerOption,
  TranscriptSegmentFragment,
} from "../../../graphql";
import { ClipRange } from "../../../types";
import useStartTime from "../../InterviewDetails/useStartTime";
import { MediaPlayerInterface } from "../../MediaPlayer/useMediaPlayer";
import TranscriptSegment from "./TranscriptSegment";
import { MatchPosition, SegmentPosition } from "./useTranscriptSearch";

export type TranscriptSegmentsProps = {
  callId?: string;
  transcript: TranscriptSegmentFragment[];
  autoScrollEnabled: boolean;
  speakers: Pick<
    CallSpeakerFragment,
    "label" | "speakerTag" | "isCandidate" | "identified"
  >[];
  player: Pick<
    MediaPlayerInterface,
    "playing" | "time" | "seek" | "play" | "duration"
  >;
  setAutoScrollEnabled(enabled: boolean): void;
  onCreateClipClick?(clipRange: ClipRange): void;
  speakerOptions?: Pick<CallSpeakerOption, "id" | "label">[];
  canChangeSpeaker?: boolean;
  notesToHighlight?: CallNoteFragment[];
  isCopyEnabled: boolean;
  calculateSegmentPositions: (transcript: TranscriptSegmentFragment[]) => void;
  matchPositions: MatchPosition[];
  segmentPositions: SegmentPosition[];
  activeMatchIndex: number;
  activeMatchPosition: MatchPosition | null;
  horizontalPadding?: number | object;
} & BoxProps;

const TranscriptSegments: React.FC<TranscriptSegmentsProps> = ({
  callId,
  transcript,
  autoScrollEnabled,
  speakers,
  player,
  notesToHighlight,
  setAutoScrollEnabled,
  onCreateClipClick,
  speakerOptions,
  canChangeSpeaker = false,
  isCopyEnabled,
  calculateSegmentPositions,
  matchPositions,
  segmentPositions,
  activeMatchIndex: _activeMatchIndex, // Destructure to prevent passing to DOM
  activeMatchPosition,
  horizontalPadding = 0,
  ...styles
}) => {
  const transcriptRef = useRef<VirtuosoHandle>(null);
  const [programmaticScroll, setProgrammaticScroll] = useState(false);

  const startTime = useStartTime(player.duration);
  const startingSegmentIndex = transcript.findIndex(
    (segment) => startTime <= segment.endTime
  );

  const checkAutoscroll = useDebouncedCallback(() => {
    if (!player.playing) {
      return;
    }
    if (programmaticScroll) {
      setProgrammaticScroll(false);
    } else {
      setAutoScrollEnabled(false);
    }
  }, 75);

  const currentSegmentIndex = useMemo(() => {
    return transcript.findIndex((segment) => player.time <= segment.endTime);
  }, [Math.round(player.time)]);

  useEffect(() => {
    calculateSegmentPositions(transcript);
  }, [transcript]);

  useEffect(() => {
    if (transcriptRef.current && activeMatchPosition) {
      transcriptRef.current.scrollToIndex({
        index: activeMatchPosition.segmentPosition?.scrollIndex ?? 0,
        align: "center",
        behavior: "auto",
      });
    }
  }, [transcriptRef, activeMatchPosition]);

  useEffect(() => {
    if (autoScrollEnabled && transcriptRef.current && currentSegmentIndex > 0) {
      setProgrammaticScroll(true);
      focusIndex(currentSegmentIndex, "smooth");
    }
  }, [autoScrollEnabled, currentSegmentIndex]);

  const seekAndPlay = (time: number, segmentIndex: number): void => {
    player.seek(time);
    player.play();
    focusIndex(segmentIndex, "smooth");
  };

  const focusIndex = (
    index: number,
    behavior: LocationOptions["behavior"] = "auto"
  ): void => {
    transcriptRef.current?.scrollToIndex({
      index,
      align: "start",
      behavior,
    });
  };

  return (
    <Box flex="1" {...styles}>
      {!autoScrollEnabled && (
        <Flex
          justifyContent="center"
          zIndex="3"
          position="fixed"
          bottom={{ base: "0", md: "6" }}
          width={{ base: "100%", lg: "60%" }}
          left="0"
        >
          <Fade
            in={!autoScrollEnabled}
            transition={{
              enter: { duration: 1 },
              exit: { duration: 1 },
            }}
            style={{ display: "contents" }}
          >
            <Button
              size="sm"
              w={{ base: "100vw", md: "auto" }}
              h={{ base: "12", md: "10", lg: "8" }}
              borderRadius={{ base: "none", md: "base" }}
              onClick={() => {
                setAutoScrollEnabled(true);
                if (!player.playing) {
                  player.play();
                }
              }}
            >
              Resume auto-scroll
            </Button>
          </Fade>
        </Flex>
      )}
      <Virtuoso
        data={transcript}
        ref={transcriptRef}
        onScroll={checkAutoscroll}
        initialTopMostItemIndex={startingSegmentIndex}
        itemContent={(index, segment) => {
          const speaker = speakers.find(
            (s) => s.speakerTag === segment.speakerTag
          );
          const currentSegment = transcript.find(
            (segment) => player.time <= segment.endTime
          );
          const isCurrentSegment =
            currentSegment?.startTime === segment.startTime;
          const matchesForSegment = matchPositions.filter(
            (pos) => pos.segmentPosition?.scrollIndex === index
          );
          const notesForSegment = notesToHighlight?.filter(
            (note) =>
              note.highlightStartTime &&
              note.highlightStartTime >= segment.startTime &&
              note.highlightEndTime &&
              note.highlightEndTime <= segment.endTime
          );
          return (
            <TranscriptSegment
              callId={callId}
              segment={segment}
              speaker={speaker}
              playerTime={isCurrentSegment ? player.time : 0}
              onSeek={seekAndPlay}
              onCreateClipClick={onCreateClipClick}
              segmentIndex={index}
              matchPositions={matchesForSegment}
              segmentPosition={segmentPositions.find(
                (pos) => pos.scrollIndex === index
              )}
              notes={notesForSegment}
              canChangeSpeaker={canChangeSpeaker}
              isCopyEnabled={isCopyEnabled}
              speakerOptions={speakerOptions}
              px={horizontalPadding}
            />
          );
        }}
      />
    </Box>
  );
};

export default TranscriptSegments;
